/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line import/no-cycle
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Container,
  ExportTableButton,
  InputTextField,
  PrintButton,
  CustomBox,
} from '@/components';
import { useAuthContext, useToastNotification } from '@/hooks';
import { ProgressSpinner } from 'primereact/progressspinner';
import { faMagnifyingGlass, faX } from '@fortawesome/free-solid-svg-icons';
import { getCardContainerData } from '@/services/getCardContainerData';
import { TableData } from '@/components/Table/types';
import { formatDateTime, formatIsoDate } from '@/helpers/date';

import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TabHeader from '@/components/TabHeader/TabHeader';

import { TabView, TabPanel } from 'primereact/tabview';
import { Divider } from 'primereact/divider';
import {
  CustomBoxProps,
  getMockData,
  getMockDataArrivo,
  getTranslatedData,
} from './ContainerTapData';

import styles from './ContainerTap.module.scss';
import TapGrid from './TapGrid';
import AdditionalData from './AdditionalData';
import { getDataTable, getLogsDataTable } from './getDataTable';

enum Tabs {
  Basic = 0,
  Trip = 1,
  Additional = 2,
}
type QueryType = {
  marca: string;
  numero: string;
};

type LogLiberacion = {
  precinto1?: string;
  precinto2?: string;
  precinto3?: string;
  precinto4?: string;
  fechaCerrado?: string;
};

type Logs = {
  usuario?: string;
  fecha?: string;
  detalle?: string;
};

const ContainerTap = () => {
  const {
    data: containerData,
    isPending: dataIsPending,
    mutate: getData,
  } = useMutation({
    mutationKey: ['containerData'],
    mutationFn: getCardContainerData,
  });
  const { t } = useTranslation('containerTap');
  const { showToast } = useToastNotification();
  const [data, setData] = useState<CustomBoxProps[]>();
  const [trips, setTrips] = useState<CustomBoxProps[]>();
  const [arrivo, setArrivo] = useState<CustomBoxProps[]>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [parteIntercambio, setParteIntercambio] = useState<LogLiberacion[]>([]);
  const [logs, seetLogs] = useState<Logs[]>([]);
  const [value, setValue] = useState('');
  const [query, setQuery] = useState<QueryType>({ marca: '', numero: '' });
  const regexValidate = /^[A-Za-z]{4}\d{7}$/;

  const firstFourItems = data?.slice(0, 4);
  const remainingItems = data?.slice(4);
  const { userDataDecoded } = useAuthContext();
  const { username } = userDataDecoded;

  const initialize = useCallback(async () => {
    if (!containerData) {
      return;
    }

    const response = await getTranslatedData(t, containerData);
    setData(response);
    const res = await getMockData(t, containerData);
    const resArrivo = await getMockDataArrivo(t, containerData);
    setArrivo(resArrivo);
    setTrips(res);
  }, [setData, t, setTrips, setArrivo, containerData]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    newValue = newValue?.replace(/[^A-Za-z0-9]/g, '');

    if (newValue.length > 11) {
      return;
    }
    setValue(newValue);
  };
  const clearInput = () => {
    setValue('');
  };
  const onSearch = () => {
    if (!regexValidate.test(value)) {
      showToast({
        severity: 'error',
        detail: t('error'),
      });
      return;
    }
    const regex = /^[^\d]*$/;
    if (typeof value !== 'string') return;
    const marca = value.slice(0, 4);
    const numero = value.slice(4);
    if (numero.length < 7) return;
    if (Number.isNaN(Number(numero))) return;

    if (!regex.test(marca)) return;
    const body = { marca, numero };

    setQuery(body);
    getData(body);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const columnsExportData = Object.keys(containerData ?? {});

  const transformLogsData = (
    logsLiberacion: LogLiberacion[]
  ): LogLiberacion[] => {
    return logsLiberacion.map((log: LogLiberacion) => {
      const precintos = [
        log.precinto1,
        log.precinto2,
        log.precinto3,
        log.precinto4,
      ]
        .filter(Boolean)
        .join(' / ');
      const fechaCerrado = formatDateTime(log.fechaCerrado ?? '');

      return {
        ...log,
        precintos,
        fechaCerrado,
      };
    });
  };

  const logsData = (logsLiberacion: Logs[]): Logs[] => {
    return logsLiberacion.map((log: Logs) => {
      const fecha = formatDateTime(log.fecha ?? '');

      return {
        ...log,
        fecha,
      };
    });
  };

  useEffect(() => {
    if (!containerData?.logsLiberacion) {
      seetLogs([]);
      setParteIntercambio([]);
      return;
    }

    const newData = transformLogsData(
      containerData?.parteIntercambio as LogLiberacion[]
    );

    setParteIntercambio(newData);

    const newDataLogs = logsData(containerData?.logsLiberacion as Logs[]);
    const updatedLogs = newDataLogs.map((lg) => ({
      ...lg,
      usuario: username,
    }));
    seetLogs(updatedLogs);
  }, [containerData, username]);

  const checkAndFormatDate = (date: any) => {
    if (!date || date === '0001-01-01T00:00:00') {
      return '';
    }

    return formatIsoDate(date || '');
  };

  const allDescriptionsEmptyFirstFourItems = firstFourItems?.every(
    (item) => !item.description
  );

  const allDescriptionsEmptyRemainingItems = remainingItems?.every(
    (item) => !item.description
  );

  return (
    <Container title={t('title')} className={styles.containerTap}>
      <div className={styles.containerSearch}>
        <div className={styles.search}>
          <div className={styles.inputContainer}>
            <InputTextField
              className={styles.inputText}
              label={t('container')}
              id="container"
              onKeyDown={handleKeyDown}
              onChange={onInputChange}
              value={value}
              name=""
            />
            {value && (
              <Button className={styles.clearButton} onClick={clearInput}>
                <FontAwesomeIcon icon={faX} />
              </Button>
            )}
          </div>

          <Button
            className={styles.button}
            onClick={onSearch}
            disabled={!regexValidate.test(value)}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span className="hidden md:block">{t('consult')}</span>
          </Button>
        </div>
        <div className={styles.containerButtons}>
          <PrintButton
            data={[containerData] as TableData}
            columnsToExport={columnsExportData}
          />
          <ExportTableButton
            data={[containerData] as TableData}
            columnsToExport={columnsExportData}
          />
        </div>
      </div>
      <div className={styles.containerTabs}>
        <div className={styles.tabsAndIdentifier}>
          <TabView
            className={styles.tabView}
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
          >
            <TabPanel
              header={
                <TabHeader
                  active={activeIndex === Tabs.Basic}
                  label={t('basicData')}
                />
              }
            >
              {activeIndex === Tabs.Basic && (
                <>
                  <div className={styles.containerMq}>
                    <h4 className={styles.titleContainer}>
                      {t('container')}{' '}
                      <span className={styles.containerData}>
                        {`${query?.marca} ${query?.numero}`}
                      </span>
                    </h4>
                  </div>
                  <h2 className={styles.subTitleOne}>
                    {!dataIsPending && t('subTitleOne')}
                  </h2>

                  <div className="flex flex-column md:flex-row ">
                    {!dataIsPending &&
                      !allDescriptionsEmptyFirstFourItems &&
                      firstFourItems?.map((item) => (
                        <div key={item.label} className={` ${item?.className}`}>
                          <CustomBox
                            key={item.label}
                            label={item.label}
                            icon={item.icon}
                            iconColor={item.iconColor}
                            description={item.description}
                            backgroundColor={item.backgroundColor}
                            iconBackgroundColor={item.iconBackgroundColor}
                          />
                        </div>
                      ))}

                    {!dataIsPending && allDescriptionsEmptyFirstFourItems && (
                      <div className="w-50 h-full flex align-items-center justify-content-center">
                        {t('noInformationAvailable')}
                      </div>
                    )}
                  </div>
                  {((!dataIsPending && data?.length) || 0) > 4 && (
                    <div className={styles.fullWidth}>
                      <Divider />
                    </div>
                  )}
                  {dataIsPending && (
                    <div className="flex justify-content-center align-items-center h-full w-full">
                      <ProgressSpinner aria-label="Loading" />
                    </div>
                  )}
                  <h3 className={styles.subTitleTwo}>
                    {!dataIsPending && t('subTitleTwo')}
                  </h3>
                  <div className="flex flex-column md:flex-row">
                    {!dataIsPending &&
                      !allDescriptionsEmptyRemainingItems &&
                      remainingItems?.map((item) => (
                        <div key={item.label} className={item?.className}>
                          <CustomBox
                            key={item.label}
                            label={item.label}
                            icon={item.icon}
                            iconColor={item.iconColor}
                            description={item.description}
                            backgroundColor={item.backgroundColor}
                            iconBackgroundColor={item.iconBackgroundColor}
                          />
                        </div>
                      ))}
                    {!dataIsPending && allDescriptionsEmptyRemainingItems && (
                      <div className="w-50 h-full flex align-items-center justify-content-center">
                        {t('noInformationAvailable')}
                      </div>
                    )}
                  </div>
                </>
              )}
            </TabPanel>
            <TabPanel
              header={
                <TabHeader
                  active={activeIndex === Tabs.Trip}
                  label={t('lastTrip')}
                />
              }
            >
              {activeIndex === Tabs.Trip && (
                <>
                  <div className={styles.containerMq}>
                    <h4 className={styles.titleContainer}>
                      {t('container')}{' '}
                      <span className={styles.containerData}>
                        {`${query?.marca} ${query?.numero}`}
                      </span>
                    </h4>
                  </div>
                  <div className="flex flex-column md:flex-row gap-4">
                    <TapGrid
                      title={t('import')}
                      items={trips ?? []}
                      date={checkAndFormatDate(
                        containerData?.llegadaImport || ''
                      )}
                      dateLabel={t('arrival')}
                    />
                    <TapGrid
                      title={t('export')}
                      items={arrivo ?? []}
                      date={checkAndFormatDate(
                        containerData?.llegadaExport || ''
                      )}
                      dateLabel={t('exit')}
                    />
                  </div>
                </>
              )}
            </TabPanel>
            <TabPanel
              header={
                <TabHeader
                  active={activeIndex === Tabs.Additional}
                  label={t('additionalData')}
                />
              }
            >
              {activeIndex === Tabs.Additional && (
                <>
                  <div className={styles.containerMq}>
                    <h4 className={styles.titleContainer}>
                      {t('container')}{' '}
                      <span className={styles.containerData}>
                        {`${query?.marca} ${query?.numero}`}
                      </span>
                    </h4>
                  </div>
                  <div className={styles.containerTable}>
                    <h2 className={styles.exchanges}>{t('exchanges')}</h2>
                    <AdditionalData
                      columns={getDataTable()}
                      data={parteIntercambio || []}
                      selectionId="parteIntercambio"
                    />
                  </div>
                  <Divider />
                  <div className={styles.containerTable}>
                    <h2 className={styles.exchanges}>{t('releaseLogs')}</h2>
                    <AdditionalData
                      columns={getLogsDataTable()}
                      data={logs || []}
                      selectionId="logsTableData"
                    />
                  </div>
                </>
              )}
            </TabPanel>
          </TabView>
          <div className={styles.containerIdentifier}>
            <h4 className={styles.titleContainer}>
              {t('container')}{' '}
              <span className={styles.containerData}>
                {`${query?.marca} ${query?.numero}`}
              </span>
            </h4>
          </div>
        </div>
      </div>
    </Container>
  );
};
export default ContainerTap;
